// ============================================================================
// PublicationComments — zone depliee commentaires d'une publication (E1b)
// ============================================================================
// Rendue dans le slot children de PublicationCard quand le viewer deplie.
// Fetch au depliage (usePublicationComments enabled) ; ajout/suppression
// OPTIMISTES avec rollback (useAccueilMutations) — l'erreur remonte en code
// court au bandeau de la page (GA-21). Les affordances viennent du SERVEUR :
// canDelete par commentaire (auteur session | admin), et si le viewer ne
// pouvait pas LIRE la publication, la route a deja repondu 404 (bind-to-fetch)
// — ici on ne calcule AUCUNE regle d'acces.

import { GsAvatar, PublicationCommentInput, QueryError } from '@gs/ui';
import type { TFunction } from 'i18next';
import { usePublicationComments, accueilErrorCode } from '../../hooks/useAccueil';
import { useAddComment, useDeleteComment, type OptimisticAuthor } from '../../hooks/useAccueilMutations';
import { formatRelative } from './format';

export function PublicationComments({
  publicationId,
  author,
  locale,
  t,
  onError,
}: {
  publicationId: string;
  author: OptimisticAuthor;
  locale: string;
  t: TFunction;
  /** Remonte le code court d'erreur au bandeau de la page (GA-21). */
  onError: (code: string) => void;
}) {
  const comments = usePublicationComments(publicationId, true);
  const addComment = useAddComment(publicationId, author);
  const deleteComment = useDeleteComment(publicationId);

  return (
    <div className="space-y-3">
      {comments.isLoading ? (
        <p className="text-field text-slate-400 dark:text-slate-500">{t('gest:accueil.feed.loading')}</p>
      ) : comments.isError ? (
        <QueryError
          message={t('gest:accueil.commentsPanel.error')}
          onRetry={() => void comments.refetch()}
        />
      ) : (
        <>
          {(comments.data?.comments.length ?? 0) === 0 ? (
            <p className="text-field text-slate-400 dark:text-slate-500">
              {t('gest:accueil.commentsPanel.empty')}
            </p>
          ) : (
            <ul className="space-y-2.5">
              {comments.data?.comments.map((c) => (
                <li key={c.id} className="flex items-start gap-2.5">
                  <GsAvatar name={c.authorName ?? c.authorEmail} size="sm" />
                  <div className="min-w-0 flex-1 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-700/50">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-field font-semibold text-slate-700 dark:text-slate-200">
                        {c.authorName ?? c.authorEmail}
                      </span>
                      <span className="text-meta text-slate-400 dark:text-slate-500">
                        {formatRelative(c.createdAt, locale)}
                      </span>
                    </div>
                    <p className="whitespace-pre-line text-body leading-relaxed text-slate-600 dark:text-slate-300">
                      {c.body}
                    </p>
                    {/* Affordance SERVEUR (canDelete) — jamais deduite du role client. */}
                    {c.canDelete && (
                      <button
                        type="button"
                        onClick={() =>
                          deleteComment.mutate(c.id, {
                            onError: (err) => onError(accueilErrorCode(err)),
                          })
                        }
                        className="mt-1 touch-manipulation text-meta font-medium text-slate-400 transition-colors hover:text-red-600 dark:text-slate-500 dark:hover:text-red-400"
                      >
                        {t('gest:accueil.commentsPanel.delete')}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <PublicationCommentInput
            authorName={author.name ?? author.email}
            placeholder={t('gest:accueil.commentsPanel.placeholder')}
            submitLabel={t('gest:accueil.commentsPanel.submit')}
            pending={addComment.isPending}
            onSubmit={(body) =>
              addComment.mutate(body, { onError: (err) => onError(accueilErrorCode(err)) })
            }
          />
        </>
      )}
    </div>
  );
}
